<template>
  <Teleport to="body">
    <Transition :name="transitionName" :css="showTransition">
      <div
        v-if="isOpen"
        id="popover-content"
        ref="content"
        :style="`${contentStyles}`"
        :class="[contentClasses]"
        @click.stop>
        <slot-listener @click.stop>
          <slot />
        </slot-listener>
      </div>
    </Transition>
  </Teleport>
</template>

<script lang="ts" setup>
import { computed, ref, toRef, onMounted, onUnmounted } from 'vue';
import type { PropType } from 'vue';
import type { DropdownPlacement, AlignPlacement } from '../Dropdown/types';
import { useDropdownClasses } from '../Dropdown/composables/useDropdownClasses';
import SlotListener from '@/components/utils/SlotListener/SlotListener.vue';

const isOpen = defineModel<boolean>({ required: true });

const props = defineProps({
  placement: {
    type: String as PropType<DropdownPlacement>,
    default: 'bottom',
  },
  align: {
    type: String as PropType<AlignPlacement>,
    default: null,
  },
  transition: {
    type: [String, null] as PropType<string | null>,
    default: null,
  },
  showTransition: {
    type: Boolean,
    default: true,
  },
  target: {
    type: Object as PropType<HTMLDivElement>,
    required: true,
  },
  triggerId: {
    type: String,
    required: true,
  },
});
const emit = defineEmits(['close']);
const placementTransitionMap: Record<DropdownPlacement, string> = {
  bottom: 'to-bottom',
  left: 'to-left',
  right: 'to-right',
  top: 'to-top',
};
const content = ref<HTMLDivElement>();
const transitionName = computed(() => {
  if (props.transition === null) return placementTransitionMap[props.placement];
  return props.transition;
});
const { contentClasses, contentStyles } = useDropdownClasses({
  placement: toRef(props, 'placement'),
  fixed: ref(true),
  align: toRef(props, 'align'),
  visible: toRef(isOpen, 'value'),
  contentRef: content,
  triggerRef: toRef(props, 'target'),
});
const handleClickOutside = (event: MouseEvent) => {
  if (!isOpen.value) return;
  const target = event.target as HTMLElement;
  const isInsidePopover = target.closest('#popover-content');
  const isTrigger = target.closest(`#${props.triggerId}`);
  const isInsidePrimeVueDropdown = target.closest('#dropdown-content');
  if (!isInsidePopover && !isTrigger && !isInsidePrimeVueDropdown) {
    isOpen.value = false;
    emit('close');
  }
};
onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});
onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>
